export type Mode = "codeproof" | "proof";
export type TrackId = "formal" | "python";
export type TrackFilter = "overall" | TrackId;

export const agentIds = ["gpt55Xhigh", "gpt55", "gpt54", "sonnet46"] as const;
export type AgentId = (typeof agentIds)[number];

export type Agent = {
  id: AgentId;
  label: string;
  shortLabel: string;
  harness: "Codex" | "Claude Code";
  model: string;
  note: string;
};

export type SummaryResult = {
  agentId: AgentId;
  mode: Mode;
  fullySolved: number;
  totalInstances: number;
  perSpecPassRate: number;
  trackSolved: Record<TrackId, { solved: number; total: number }>;
  costUsd: number;
};

export type InstanceScore = {
  passed: number;
  total: number;
};

export type InstanceResult = {
  instance: string;
  track: TrackId;
  sourceLanguage: "Dafny" | "Verus" | "Coq" | "Python";
  totalSpecs: number;
  scores: Record<AgentId, Record<Mode, InstanceScore>>;
  tags: string[];
  note: string;
};

export type CaseStudy = {
  id: string;
  title: string;
  instance: string;
  headline: string;
  stats: { label: string; value: string }[];
  story: string;
  visualType: "proof-stack" | "contradiction" | "helper-extraction" | "partial-progress";
};

export type CtaLink = {
  id: "paper" | "code" | "dataset" | "harness";
  title: string;
  body: string;
  href?: string;
  action: string;
  unavailableLabel: string;
};

type RawScore = Record<AgentId, Record<Mode, number>>;

const ctaHref = (value: string | undefined) => (value && value !== "#" ? value : undefined);

const score = (
  gpt55XhighCp: number,
  gpt55XhighPo: number,
  gpt55Cp: number,
  gpt55Po: number,
  gpt54Cp: number,
  gpt54Po: number,
  sonnet46Cp: number,
  sonnet46Po: number
): RawScore => ({
  gpt55Xhigh: { codeproof: gpt55XhighCp, proof: gpt55XhighPo },
  gpt55: { codeproof: gpt55Cp, proof: gpt55Po },
  gpt54: { codeproof: gpt54Cp, proof: gpt54Po },
  sonnet46: { codeproof: sonnet46Cp, proof: sonnet46Po }
});

const instance = (
  instanceName: string,
  track: TrackId,
  sourceLanguage: InstanceResult["sourceLanguage"],
  totalSpecs: number,
  raw: RawScore,
  tags: string[],
  note: string
): InstanceResult => ({
  instance: instanceName,
  track,
  sourceLanguage,
  totalSpecs,
  tags,
  note,
  scores: Object.fromEntries(
    agentIds.map((agentId) => [
      agentId,
      {
        codeproof: { passed: raw[agentId].codeproof, total: totalSpecs },
        proof: { passed: raw[agentId].proof, total: totalSpecs }
      }
    ])
  ) as Record<AgentId, Record<Mode, InstanceScore>>
});

export const verify0Data = {
  summary: {
    title: "Verify0",
    headline: "Can AI agents build formally verified software repositories?",
    subheadline:
      "Verify0 is a Lean 4 benchmark for repository-scale verified code generation. Agents must synthesize implementations, prove formal specifications, and pass clean-sandbox grading across real multi-module codebases.",
    instances: 35,
    formalInstances: 20,
    pythonInstances: 15,
    modules: 285,
    specifications: 1734,
    sourceFamilies: ["Python", "Dafny", "Verus", "Coq"],
    bestFullSolve: { solved: 18, total: 35, agentId: "gpt55Xhigh" as AgentId, mode: "codeproof" as Mode },
    bestProofOnly: { solved: 14, total: 35, agentId: "gpt55Xhigh" as AgentId, mode: "proof" as Mode },
    bestPassRate: { value: 62, agentId: "gpt55Xhigh" as AgentId, mode: "codeproof" as Mode },
    zeroSpecRepositories: 4,
    auditFindings: 28,
    auditInstances: 8,
    leanVersion: "Lean v4.29.1",
    codexVersion: "Codex v0.125.0",
    claudeCodeVersion: "Claude Code v2.1.126"
  },
  modes: {
    codeproof: {
      id: "codeproof" as Mode,
      label: "Code-and-proof",
      shortLabel: "CP",
      color: "#0ea5e9",
      description:
        "Agents synthesize API implementations and Lean proofs, measuring implementation/proof co-design."
    },
    proof: {
      id: "proof" as Mode,
      label: "Proof-only",
      shortLabel: "PO",
      color: "#7c3aed",
      description:
        "Reference implementations are fixed; agents only discharge proof obligations over the provided code."
    }
  },
  tracks: {
    formal: {
      id: "formal" as TrackId,
      label: "Track 1",
      title: "Formal-source repositories",
      shortTitle: "Formal source",
      languages: "Dafny, Verus, Coq",
      instances: 20,
      meanModules: 9.2,
      meanApis: 29.7,
      meanSpecs: 48.5,
      maxSpecs: 129,
      interpretation:
        "These instances inherit invariant structures from real verified projects, including protocol-level properties and cross-module consistency conditions."
    },
    python: {
      id: "python" as TrackId,
      label: "Track 2",
      title: "Python-source repositories",
      shortTitle: "Python source",
      languages: "Python",
      instances: 15,
      meanModules: 6.6,
      meanApis: 23.3,
      meanSpecs: 51,
      maxSpecs: 115,
      interpretation:
        "These instances often let strong agents rewrite implementations into proof-friendly Lean while preserving curated specifications."
    }
  },
  agents: [
    {
      id: "gpt55Xhigh",
      label: "GPT-5.5 xhigh",
      shortLabel: "GPT-5.5 xh",
      harness: "Codex",
      model: "GPT-5.5",
      note: "Codex with xhigh reasoning effort"
    },
    {
      id: "gpt55",
      label: "GPT-5.5",
      shortLabel: "GPT-5.5",
      harness: "Codex",
      model: "GPT-5.5",
      note: "Codex with default reasoning effort"
    },
    {
      id: "gpt54",
      label: "GPT-5.4",
      shortLabel: "GPT-5.4",
      harness: "Codex",
      model: "GPT-5.4",
      note: "Codex with default reasoning effort"
    },
    {
      id: "sonnet46",
      label: "Sonnet 4.6",
      shortLabel: "Sonnet 4.6",
      harness: "Claude Code",
      model: "Claude Sonnet 4.6",
      note: "Claude Code with default thinking budget"
    }
  ] satisfies Agent[],
  results: [
    {
      agentId: "gpt55Xhigh",
      mode: "codeproof",
      fullySolved: 18,
      totalInstances: 35,
      perSpecPassRate: 62,
      trackSolved: { formal: { solved: 8, total: 20 }, python: { solved: 10, total: 15 } },
      costUsd: 309
    },
    {
      agentId: "gpt55Xhigh",
      mode: "proof",
      fullySolved: 14,
      totalInstances: 35,
      perSpecPassRate: 54,
      trackSolved: { formal: { solved: 8, total: 20 }, python: { solved: 6, total: 15 } },
      costUsd: 292
    },
    {
      agentId: "gpt55",
      mode: "codeproof",
      fullySolved: 14,
      totalInstances: 35,
      perSpecPassRate: 50,
      trackSolved: { formal: { solved: 8, total: 20 }, python: { solved: 6, total: 15 } },
      costUsd: 439
    },
    {
      agentId: "gpt55",
      mode: "proof",
      fullySolved: 10,
      totalInstances: 35,
      perSpecPassRate: 41,
      trackSolved: { formal: { solved: 8, total: 20 }, python: { solved: 2, total: 15 } },
      costUsd: 336
    },
    {
      agentId: "gpt54",
      mode: "codeproof",
      fullySolved: 3,
      totalInstances: 35,
      perSpecPassRate: 12,
      trackSolved: { formal: { solved: 3, total: 20 }, python: { solved: 0, total: 15 } },
      costUsd: 175
    },
    {
      agentId: "gpt54",
      mode: "proof",
      fullySolved: 5,
      totalInstances: 35,
      perSpecPassRate: 26,
      trackSolved: { formal: { solved: 5, total: 20 }, python: { solved: 0, total: 15 } },
      costUsd: 141
    },
    {
      agentId: "sonnet46",
      mode: "codeproof",
      fullySolved: 4,
      totalInstances: 35,
      perSpecPassRate: 8,
      trackSolved: { formal: { solved: 4, total: 20 }, python: { solved: 0, total: 15 } },
      costUsd: 1054
    },
    {
      agentId: "sonnet46",
      mode: "proof",
      fullySolved: 6,
      totalInstances: 35,
      perSpecPassRate: 18,
      trackSolved: { formal: { solved: 5, total: 20 }, python: { solved: 1, total: 15 } },
      costUsd: 910
    }
  ] satisfies SummaryResult[],
  instances: [
    instance("FEUP_MFS_Dafny", "formal", "Dafny", 19, score(19, 19, 19, 19, 3, 6, 0, 6), ["solved"], "Small Dafny file-system model; solved by the strongest Codex rows."),
    instance("ParcelLocker", "formal", "Dafny", 2, score(2, 2, 2, 2, 2, 2, 2, 2), ["solved"], "Tiny formal-source instance closed by every evaluated cell."),
    instance("arithmetic", "formal", "Verus", 63, score(18, 0, 16, 0, 0, 0, 0, 0), ["math-theory"], "Verus arithmetic obligations require custom mathematical reasoning."),
    instance("dedekind-reals", "formal", "Coq", 83, score(0, 0, 0, 0, 0, 0, 0, 0), ["zero-spec", "math-theory"], "Hard zero example from a custom real-analysis development."),
    instance("deposit_sc", "formal", "Dafny", 79, score(72, 64, 26, 39, 1, 15, 24, 26), ["case-study", "induction"], "Layered Merkle proof library; remaining specs share one missing boundary-shift invariant."),
    instance("eip20", "formal", "Coq", 26, score(26, 26, 26, 26, 0, 3, 0, 0), ["case-study", "solved"], "ERC-20 state-machine reasoning where helper extraction separates strong agents."),
    instance("escrow", "formal", "Coq", 7, score(7, 7, 7, 7, 0, 7, 7, 7), ["solved"], "Small branch-heavy smart-contract instance largely closed by case analysis."),
    instance("eth2.0-dafny", "formal", "Dafny", 69, score(0, 0, 0, 0, 0, 0, 0, 0), ["zero-spec", "protocol"], "Hard zero example with protocol-level invariant structure."),
    instance("flocq", "formal", "Coq", 103, score(0, 0, 0, 0, 0, 0, 0, 0), ["zero-spec", "math-theory"], "Hard zero example requiring floating-point mathematical theory."),
    instance("huffman", "formal", "Coq", 42, score(0, 0, 0, 0, 0, 0, 0, 0), ["zero-spec", "induction"], "Hard zero example involving inductive coding/tree theory."),
    instance("json", "formal", "Dafny", 67, score(1, 7, 18, 6, 0, 1, 0, 0), ["parser"], "Parser/data-format reasoning with uneven partial progress."),
    instance("leftpad", "formal", "Verus", 6, score(6, 6, 6, 6, 6, 6, 6, 6), ["solved"], "Small formal-source instance closed across all evaluated cells."),
    instance("lets-prove-leftpad", "formal", "Dafny", 6, score(6, 6, 6, 6, 6, 6, 6, 0), ["solved"], "Leftpad variant mostly solved, except one Sonnet proof-only cell."),
    instance("piggybank", "formal", "Coq", 7, score(7, 7, 7, 7, 0, 7, 0, 7), ["solved"], "Branchy smart-contract instance handled by local reasoning."),
    instance("sequences", "formal", "Dafny", 53, score(34, 39, 25, 29, 1, 11, 0, 0), ["partial"], "Sequence-library reasoning shows substantial but incomplete progress."),
    instance("unicode", "formal", "Dafny", 10, score(10, 10, 10, 10, 3, 4, 0, 10), ["solved"], "Small Unicode instance solved by the strongest Codex rows and Sonnet proof-only."),
    instance("verdict", "formal", "Verus", 120, score(108, 107, 101, 0, 0, 0, 0, 0), ["case-study", "audit", "parser"], "Parser/byte-range reasoning; Base64 contradiction caught by audit proof."),
    instance("verified-bitmasks", "formal", "Dafny", 129, score(0, 5, 0, 0, 7, 53, 46, 4), ["partial"], "Bit-mask verification has high specification count and uneven agent behavior."),
    instance("verified_ironkv", "formal", "Verus", 33, score(3, 0, 0, 0, 0, 0, 0, 0), ["audit", "induction"], "Small but hard distributed-systems instance with comparator-lawfulness audit findings."),
    instance("vest", "formal", "Verus", 45, score(41, 41, 36, 36, 18, 26, 0, 2), ["parser"], "Parser branch reasoning; strong agents close 41 of 45 specs."),
    instance("bidict", "python", "Python", 71, score(71, 71, 69, 70, 35, 67, 0, 0), ["solved", "audit"], "Bidirectional-map instance; audit added non-conflict preconditions."),
    instance("bit_manipulation", "python", "Python", 63, score(33, 24, 17, 12, 3, 0, 0, 21), ["audit"], "Bit manipulation specs include domain-restriction audit fixes."),
    instance("bitlist", "python", "Python", 16, score(16, 16, 16, 16, 0, 14, 2, 0), ["solved"], "Compact bit-list instance solved by stronger agents."),
    instance("boolean_algebra", "python", "Python", 45, score(45, 41, 45, 41, 36, 33, 0, 16), ["solved"], "Boolean-algebra APIs are tractable for stronger code-and-proof runs."),
    instance("compression", "python", "Python", 32, score(32, 32, 32, 17, 0, 11, 0, 0), ["solved"], "Compression utilities show local reasoning success for stronger agents."),
    instance("heap", "python", "Python", 104, score(89, 99, 64, 76, 8, 16, 0, 13), ["case-study", "audit", "global-invariant"], "High partial progress hides missing global heap-ordering and sortedness invariants."),
    instance("linked_list", "python", "Python", 105, score(105, 100, 90, 84, 0, 46, 0, 48), ["solved", "audit"], "Linked-list repository solved in code-and-proof by the strongest agent."),
    instance("number_theory", "python", "Python", 39, score(39, 4, 20, 4, 1, 3, 0, 0), ["solved"], "Code-and-proof implementation freedom helps the strongest agent close the repository."),
    instance("primepy", "python", "Python", 8, score(7, 1, 4, 1, 2, 1, 1, 1), ["partial"], "Small instance that still leaves nontrivial number-theory obligations."),
    instance("queues", "python", "Python", 115, score(115, 115, 115, 112, 37, 75, 0, 115), ["solved"], "Large but branch-heavy data-structure repository; many cells close or nearly close it."),
    instance("sortedcontainers", "python", "Python", 91, score(85, 28, 11, 29, 0, 7, 0, 5), ["partial"], "Implementation freedom helps: 85/91 in code-and-proof versus 28/91 proof-only for GPT-5.5 xhigh."),
    instance("special_numbers", "python", "Python", 23, score(23, 13, 22, 11, 0, 2, 15, 1), ["solved", "audit"], "Special-number predicates include one spec replacement from audit findings."),
    instance("stack", "python", "Python", 31, score(31, 31, 31, 31, 25, 24, 22, 0), ["solved"], "Stack APIs are largely local and tractable for most agents."),
    instance("suffix_tree", "python", "Python", 10, score(10, 10, 10, 9, 0, 0, 0, 0), ["solved"], "Compact suffix-tree instance solved by stronger agents."),
    instance("toposort", "python", "Python", 12, score(9, 6, 8, 5, 0, 5, 4, 0), ["partial"], "Topological-sort specs show partial but incomplete closure.")
  ] satisfies InstanceResult[],
  audit: {
    findings: 28,
    instances: 8,
    fixed: "All addressed in the released benchmark",
    routes: [
      {
        title: "Reference implementation bug",
        notation: "not all specs hold on canonical",
        description: "The reference implementation fails one or more specifications."
      },
      {
        title: "Unsatisfiable specification",
        notation: "not exists impl, S impl",
        description: "No implementation can satisfy the specification."
      },
      {
        title: "Jointly inconsistent specs",
        notation: "each spec satisfiable, but not together",
        description: "The specifications are individually satisfiable but contradictory as a set."
      }
    ],
    distribution: [
      { instance: "verdict", route: "joint_unsat + disprove", specs: "1 pair", fix: "one-line predicate fix" },
      { instance: "verified_ironkv", route: "unsat", specs: "3", fix: "LawfulKeyTrait side condition" },
      { instance: "heap", route: "disprove", specs: "13", fix: "structural-validity preconditions" },
      { instance: "deposit_sc", route: "disprove", specs: "4", fix: "well-formedness preconditions" },
      { instance: "bit_manipulation", route: "disprove", specs: "3", fix: "domain restriction" },
      { instance: "bidict", route: "disprove", specs: "2", fix: "non-conflict preconditions" },
      { instance: "linked_list", route: "disprove", specs: "1", fix: "uniqueness precondition" },
      { instance: "special_numbers", route: "unsat + disprove", specs: "1", fix: "spec replacement" }
    ]
  },
  caseStudies: [
    {
      id: "deposit-sc",
      title: "deposit_sc",
      instance: "deposit_sc",
      headline: "Layered proof library, one missing invariant",
      stats: [
        { label: "Specs closed", value: "72 / 79" },
        { label: "Helper lemmas", value: "64" },
        { label: "Proof library", value: "~1,130 lines" },
        { label: "Modules", value: "4" }
      ],
      story:
        "GPT-5.5 xhigh assembled bit-list, tree, Merkle, and contract helper layers. The remaining seven specs depend on one missing invariant about how Merkle-path siblings evolve when a leaf array grows.",
      visualType: "proof-stack"
    },
    {
      id: "verdict",
      title: "verdict",
      instance: "verdict",
      headline: "Audit proof catches a Base64 contradiction",
      stats: [
        { label: "Best run", value: "108 / 120" },
        { label: "Audit route", value: "joint_unsat" },
        { label: "Fix", value: "1-line predicate" }
      ],
      story:
        "One spec required '=' to return some none, while another reject predicate classified '=' as invalid and required none. The joint-unsat proof exposed the contradiction.",
      visualType: "contradiction"
    },
    {
      id: "eip20",
      title: "eip20",
      instance: "eip20",
      headline: "Reusable helper extraction closes the contract",
      stats: [
        { label: "Specs", value: "26" },
        { label: "Strong runs", value: "26 / 26" },
        { label: "Source", value: "ConCert" }
      ],
      story:
        "Strong agents extracted helper lemmas summarizing successful contract calls, then reused them across balance, allowance, and frame-condition proofs.",
      visualType: "helper-extraction"
    },
    {
      id: "heap",
      title: "heap",
      instance: "heap",
      headline: "Partial progress hides the global invariant",
      stats: [
        { label: "Code-and-proof", value: "89 / 104" },
        { label: "Best cell", value: "99 / 104" },
        { label: "Audit fixes", value: "13 specs" }
      ],
      story:
        "The unclosed obligations require global heap-ordering and sortedness invariants. This is why Verify0 treats full repository closure as the headline metric.",
      visualType: "partial-progress"
    }
  ] satisfies CaseStudy[],
  ctas: [
    {
      id: "paper",
      title: "Paper",
      body: "Benchmark paper with corpus details, result figures, cost table, audit appendix, and per-instance counts.",
      href: ctaHref(import.meta.env.VITE_VERIFY0_PAPER_URL),
      action: "Read paper",
      unavailableLabel: "Paper URL pending"
    },
    {
      id: "code",
      title: "Code",
      body: "Lean benchmark scaffolds, curation pipeline, generation/evaluation code, and release branch.",
      href: ctaHref(import.meta.env.VITE_VERIFY0_CODE_URL),
      action: "Browse code",
      unavailableLabel: "Code URL pending"
    },
    {
      id: "dataset",
      title: "Dataset",
      body: "The 35-instance corpus split into formal-source and Python-source tracks.",
      href: ctaHref(import.meta.env.VITE_VERIFY0_DATASET_URL),
      action: "Open dataset",
      unavailableLabel: "Dataset URL pending"
    },
    {
      id: "harness",
      title: "Evaluation harness",
      body: "Fresh-sandbox grading for proof-only and code-and-proof submissions.",
      href: ctaHref(import.meta.env.VITE_VERIFY0_HARNESS_URL),
      action: "Run harness",
      unavailableLabel: "Harness URL pending"
    }
  ] satisfies CtaLink[],
  citation:
    "Verify0: Can AI Agents Build Formally Verified Software Repositories? NeurIPS 2026 submission."
} as const;

export const modeIds = ["codeproof", "proof"] as const;
export const trackFilters = ["overall", "formal", "python"] as const;

export function agentById(agentId: AgentId): Agent {
  return verify0Data.agents.find((agent) => agent.id === agentId) ?? verify0Data.agents[0];
}

export function resultFor(agentId: AgentId, mode: Mode): SummaryResult {
  return (
    verify0Data.results.find((result) => result.agentId === agentId && result.mode === mode) ??
    verify0Data.results[0]
  );
}

export function solvedFor(agentId: AgentId, mode: Mode, track: TrackFilter): number {
  const result = resultFor(agentId, mode);
  if (track === "overall") {
    return result.fullySolved;
  }
  return result.trackSolved[track].solved;
}

export function denominatorFor(track: TrackFilter): number {
  if (track === "formal") {
    return verify0Data.tracks.formal.instances;
  }
  if (track === "python") {
    return verify0Data.tracks.python.instances;
  }
  return verify0Data.summary.instances;
}

export function passRateFor(agentId: AgentId, mode: Mode, track: TrackFilter): number {
  if (track === "overall") {
    return resultFor(agentId, mode).perSpecPassRate;
  }
  const rows = verify0Data.instances.filter((row) => row.track === track);
  const passed = rows.reduce((sum, row) => sum + row.scores[agentId][mode].passed, 0);
  const total = rows.reduce((sum, row) => sum + row.totalSpecs, 0);
  return total === 0 ? 0 : Math.round((passed / total) * 100);
}

export function modeLabel(mode: Mode) {
  return verify0Data.modes[mode].label;
}

export function trackLabel(track: TrackFilter) {
  if (track === "overall") {
    return "Overall";
  }
  return verify0Data.tracks[track].shortTitle;
}

export function instanceBest(row: InstanceResult) {
  const candidates = agentIds.flatMap((agentId) =>
    modeIds.map((mode) => ({
      agentId,
      mode,
      passed: row.scores[agentId][mode].passed,
      total: row.totalSpecs
    }))
  );
  return candidates.sort((a, b) => b.passed - a.passed)[0];
}

export function isSolvedByAny(row: InstanceResult) {
  return agentIds.some((agentId) => modeIds.some((mode) => row.scores[agentId][mode].passed === row.totalSpecs));
}

export function isZeroByAll(row: InstanceResult) {
  return agentIds.every((agentId) => modeIds.every((mode) => row.scores[agentId][mode].passed === 0));
}
